import React from 'react'
import { observer } from 'mobx-react'
import { observable } from 'mobx'
import ReactSlick from 'react-slick'
import { Button } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import parse from 'html-react-parser'
import { Ratings } from 'src/views/components/ReviewSection/Ratings'
import { NuskinPlusCartIcon } from 'src/views/components/Header/Components/CartIcon/NuskinCartIcon'
import { Arrows } from 'src/views/components/Icons/Arrows'
import { catalogDeps } from 'src/deps'
import {
  getImageFromProperty,
  ApiContent,
  trackProductClick,
  convertMarkdownToHtml,
  i18nTranslate,
} from 'src/utils'
import { getLocaleCodeFromUrl } from 'src/utils/localeUtils'
import { imagePlaceholder } from 'src/static/fixtures/imagePlaceholder'
import { ImageComponent } from 'src/views/components'
import { pageNames } from 'src/routes/pathParams'
import RelatedProductPrice from './RelatedProductPrice'
import './RelatedProductCarousel.scss'

@observer
class RelatedProductCarousel extends React.Component {
  static contextType = ApiContent
  @observable currentSlide = 0
  @observable cardHeight = 'auto'
  @observable imageLoadingStates = new Map()

  cardRefs = []
  resizeObserver = null
  slickRef = null

  componentDidMount() {
    this.setupHeightSync()
  }

  CustomPrevArrow = ({ onClick }) => {
    const { products, slidesToShow } = this.props
    const isAtStart = this.currentSlide === 0

    return (
      <Button
        variant="link"
        className="carousel-arrow carousel-prev"
        onClick={onClick}
        disabled={isAtStart}
        aria-label="Previous products">
        <Arrows icontype="backwardarrow" width="20px" color="currentColor" />
      </Button>
    )
  }

  CustomNextArrow = ({ onClick }) => {
    const { products, slidesToShow } = this.props
    const effectiveSlidesToShow = Math.min(slidesToShow, products.length)
    const maxSlide = Math.max(0, products.length - effectiveSlidesToShow)
    const isAtEnd = this.currentSlide >= maxSlide

    return (
      <Button
        variant="link"
        className="carousel-arrow carousel-next"
        onClick={onClick}
        disabled={isAtEnd}
        aria-label="Next products">
        <Arrows icontype="forwardarrow" width="20px" color="currentColor" />
      </Button>
    )
  }

  CustomRightArrow = ({ onClick }) => {
    const { products, slidesToShow } = this.props
    const effectiveSlidesToShow = Math.min(slidesToShow, products.length)
    const maxSlide = Math.max(0, products.length - effectiveSlidesToShow)
    const isAtEnd = this.currentSlide >= maxSlide

    return (
      <Button
        variant="link"
        className="carousel-arrow carousel-right-only"
        onClick={onClick}
        disabled={isAtEnd}
        aria-label="Next products">
        <Arrows icontype="rightarrow" width="20px" color="currentColor" />
      </Button>
    )
  }

  static defaultProps = {
    products: [],
    onAddToCart: () => {},
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: false,
    autoplaySpeed: 3000,
    showArrows: true,
  }

  componentDidUpdate(prevProps) {
    if (prevProps.products !== this.props.products) {
      this.setupHeightSync()
    }
  }

  componentWillUnmount() {
    this.cleanupHeightSync()
  }
  setupHeightSync = () => {
    this.cardRefs = []
    if (typeof ResizeObserver !== 'undefined') {
      this.cleanupHeightSync()
      this.resizeObserver = new ResizeObserver(this.calculateMaxHeight)
    }
    setTimeout(() => {
      this.calculateMaxHeight()
    }, 100)
  }

  cleanupHeightSync = () => {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  }

  calculateMaxHeight = () => {
    if (!this.cardRefs.length) return
    this.cardRefs.forEach(ref => {
      if (ref) {
        ref.style.height = 'auto'
      }
    })

    this.cardRefs[0]?.offsetHeight
    let maxHeight = 0
    this.cardRefs.forEach(ref => {
      if (ref) {
        const height = ref.offsetHeight
        maxHeight = Math.max(maxHeight, height)
      }
    })

    if (maxHeight > 0) {
      this.cardHeight = `${maxHeight}px`
      this.cardRefs.forEach(ref => {
        if (ref) {
          ref.style.height = this.cardHeight
        }
      })
    }
  }

  setCardRef = (ref, index) => {
    if (ref) {
      this.cardRefs[index] = ref
      if (this.resizeObserver) {
        this.resizeObserver.observe(ref)
      }
      if (this.cardRefs.filter(Boolean).length === this.props.products.length) {
        setTimeout(this.calculateMaxHeight, 50)
      }
    }
  }

  handleSlideChange = current => {
    this.currentSlide = current
    if (this.props.onSlideChange) {
      this.props.onSlideChange(current)
    }
  }

  slickNext = () => {
    if (this.slickRef) {
      this.slickRef.slickNext()
    }
  }

  slickPrev = () => {
    if (this.slickRef) {
      this.slickRef.slickPrev()
    }
  }

  slickGoTo = slideNumber => {
    if (this.slickRef) {
      this.slickRef.slickGoTo(slideNumber)
    }
  }

  setImageLoading = (identifier, isLoading) => {
    this.imageLoadingStates.set(identifier, isLoading)
  }

  isImageLoading = identifier => {
    return this.imageLoadingStates.get(identifier) !== false
  }

  isPlaceholderImage = imageURL => {
    return (
      !imageURL ||
      imageURL === imagePlaceholder ||
      imageURL.includes('placeholder') ||
      imageURL.includes('imageNotFound')
    )
  }

  sanitizeImageUrl = url => {
    if (!url || typeof url !== 'string') return null

    try {
      url = url.trim().replace(/^["']+|["']+$/g, '')
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url
      }
      if (url.startsWith('/')) {
        return url
      }
      if (url.startsWith('data:')) {
        return url
      }
      return null
    } catch (e) {
      console.warn('Error sanitizing image URL:', e)
      return null
    }
  }

  getProductImage = product => {
    const defaultSku = catalogDeps.getDefaultSelectedSku(product)
    let imageURL = getImageFromProperty(defaultSku?.[0]?.properties)

    if (!imageURL) {
      imageURL = getImageFromProperty(product?.properties)
    }

    if (!imageURL) {
      imageURL =
        product?.properties?.primaryimage ||
        product?.properties?.imageURL ||
        product?.properties?.image
    }
    imageURL = this.sanitizeImageUrl(imageURL)
    if (!imageURL) {
      imageURL = imagePlaceholder
    }
    return imageURL
  }

  extractProductData = product => {
    const defaultSku = catalogDeps.getDefaultSelectedSku(product)
    const skuName = defaultSku?.[0]?.properties?.name || ''
    const { name = skuName, brand } = product?.properties || {}

    const identifier = this.props?.productId || product?.identifier || ''
    const slug = product?.properties?.slug
    const productOrSlugId = slug || identifier
    const skuList = product?.sku || []
    const skuId = defaultSku?.[0]?.identifier || ''
    const skuSlug = defaultSku?.[0]?.properties?.slug || ''
    const variantParameter = skuSlug || skuId || ''
    const productType = product?.properties?.producttype || ''
    const imageURL = this.getProductImage(product)
    const isPlaceholder = this.isPlaceholderImage(imageURL)

    return {
      name,
      brand,
      identifier,
      productOrSlugId,
      skuId,
      variantParameter,
      productType,
      imageURL,
      isPlaceholder,
      skuList,
      defaultSku,
    }
  }

  handleProductClick = product => {
    const productUrl = this.getProductUrl(product)
    trackProductClick({
      type: 'productClick',
      action: { list: 'Related Products' },
      product,
    })
    window.location.href = productUrl
  }

  getProductUrl = product => {
    const defaultSku = catalogDeps.getDefaultSelectedSku(product)
    const identifier = product?.identifier || ''
    const slug = product?.properties?.slug
    const productOrSlugId = slug || identifier
    const skuId = defaultSku?.[0]?.identifier || ''
    const skuSlug = defaultSku?.[0]?.properties?.slug || ''
    const variantParameter = skuSlug || skuId || ''
    const hasMoreThanOneSku = (product?.sku?.length || 1) > 1
    const shouldAppendVariant = hasMoreThanOneSku && variantParameter
    // access only if window object exists
    if (typeof window !== 'undefined' && window.location) {
      const { origin } = window.location
      const locale = getLocaleCodeFromUrl ? getLocaleCodeFromUrl() : ''
      const [language, country] = locale ? locale.split('_') : ['en', 'us']
      const regionPath =
        country && language
          ? `/${country.toLowerCase()}/${language.toLowerCase()}`
          : ''
      if (shouldAppendVariant) {
        return `${origin}${regionPath}${pageNames.product}/${productOrSlugId}?variant=${variantParameter}`
      } else {
        return `${origin}${regionPath}${pageNames.product}/${productOrSlugId}`
      }
    }
    // fallback if window is not available
    return ''
  }

  renderProductCard = (product, index) => {
    const productData = this.extractProductData(product)
    const { name, identifier, productType, imageURL, isPlaceholder, skuId } =
      productData

    const isLoading = this.isImageLoading(identifier)
    const productUrl = this.getProductUrl(product)

    return (
      <div key={skuId || identifier} className="related-carousel-item">
        <div
          className="related-product-card"
          ref={ref => this.setCardRef(ref, index)}>
          <div className={`image-container-wrap ${isLoading ? 'loading' : ''}`}>
            <img
              src={imageURL}
              onClick={() => this.handleProductClick(product)}
              alt={name || 'Product Image'}
              className={`product-image ${isPlaceholder ? 'placeholder' : ''}`}
              loading="lazy"
              data-testid={`product-image-${identifier}`}
              onError={e => {
                e.target.src = imagePlaceholder
                e.target.className += ' placeholder'
                this.setImageLoading(identifier, false)
              }}
              onLoad={() => {
                this.setImageLoading(identifier, false)
              }}
            />
            {isLoading && (
              <div className="image-loading-indicator">
                <div className="loading-spinner"></div>
              </div>
            )}
          </div>

          <div className="product-details">
            {this.renderRatingSection(product)}

            <h3
              className="product-name product-name-link"
              data-testid={`product-name-${identifier}`}
              title={name}
              onClick={() => this.handleProductClick(product)}>
              {name}
            </h3>

            {this.renderProductDescription(product)}

            {this.renderBundleDiscount(productType)}

            <RelatedProductPrice product={product} productType={productType} />

            {this.renderAddToCartButton(product)}
          </div>
        </div>
      </div>
    )
  }

  renderRatingSection = product => {
    if (!product?.rating) return null

    return (
      <div className="rating-section">
        <div className="stars">
          <Ratings starRating={product.rating || 0} starCount={5} />
        </div>
        <span className="review-count">
          ({product.reviewCount || 0} Reviews)
        </span>
      </div>
    )
  }

  renderProductDescription = product => {
    const description =
      product?.properties?.description ||
      product?.properties?.shortDescription ||
      ''

    if (!description || typeof description !== 'string') return null

    const htmlContent = convertMarkdownToHtml(description)

    return (
      <div className="product-description">
        <p
          className="description-text"
          title={description.replace(/\*\*([^*]+)\*\*/g, '$1')}>
          {parse(htmlContent)}
        </p>
      </div>
    )
  }

  renderBundleDiscount = productType => {
    const isBundleOrKit = productType === 'bundle' || productType === 'kit'

    if (!isBundleOrKit) return null

    return (
      <div className="bundle-discount-badge">
        {i18nTranslate('common.bundleDiscount', 'Bundle Discount')}
      </div>
    )
  }

  renderAddToCartButton = product => {
    return (
      <Button
        variant="outline-primary"
        className="add-button"
        onClick={() => this.props.onAddToCart(product)}
        aria-label={`Add ${product?.properties?.name || 'product'} to cart`}>
        <NuskinPlusCartIcon />
        {i18nTranslate('add_text', 'Add')}
      </Button>
    )
  }

  getCarouselSettings = () => {
    const { products, slidesToShow, autoplay, autoplaySpeed, showArrows } =
      this.props
    const needsNavigation = products.length > slidesToShow
    const effectiveSlidesToShow = Math.min(slidesToShow, products.length)

    return {
      dots: false,
      infinite: false,
      speed: 300,
      slidesToShow: effectiveSlidesToShow,
      slidesToScroll: 1,
      autoplay: autoplay && needsNavigation,
      autoplaySpeed: autoplaySpeed,
      arrows: showArrows && needsNavigation,
      prevArrow:
        showArrows && needsNavigation ? <this.CustomPrevArrow /> : null,
      nextArrow:
        showArrows && needsNavigation ? <this.CustomNextArrow /> : null,
      afterChange: this.handleSlideChange,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: Math.min(4, products.length),
            slidesToScroll: 1,
            arrows: showArrows && products.length > 3,
            dots: false,
            infinite: false,
            prevArrow:
              showArrows && products.length > 3 ? (
                <this.CustomPrevArrow />
              ) : null,
            nextArrow:
              showArrows && products.length > 3 ? (
                <this.CustomNextArrow />
              ) : null,
            afterChange: this.handleSlideChange,
          },
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: Math.min(2, products.length),
            slidesToScroll: 1,
            arrows: showArrows && products.length > 2,
            dots: false,
            infinite: false,
            prevArrow:
              showArrows && products.length > 2 ? (
                <this.CustomPrevArrow />
              ) : null,
            nextArrow:
              showArrows && products.length > 2 ? (
                <this.CustomNextArrow />
              ) : null,
            afterChange: this.handleSlideChange,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: showArrows && products.length > 1,
            dots: false,
            infinite: products.length > 1,
            prevArrow: null,
            nextArrow:
              showArrows && products.length > 1 ? (
                <this.CustomRightArrow />
              ) : null,
            afterChange: this.handleSlideChange,
          },
        },
        {
          breakpoint: 420,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: showArrows && products.length > 1,
            dots: false,
            infinite: products.length > 1,
            prevArrow: null,
            nextArrow:
              showArrows && products.length > 1 ? (
                <this.CustomRightArrow />
              ) : null,
            afterChange: this.handleSlideChange,
          },
        },
      ],
    }
  }

  render() {
    const { products } = this.props

    if (!products || products.length === 0) {
      return null
    }

    const carouselSettings = this.getCarouselSettings()

    return (
      <div className="related-product-carousel-wrapper equal-height-cards">
        <ReactSlick
          {...carouselSettings}
          ref={slider => {
            this.slickRef = slider
            if (this.props.forwardedRef) {
              this.props.forwardedRef.current = {
                slickNext: () => this.slickNext(),
                slickPrev: () => this.slickPrev(),
                slickGoTo: slideNumber => this.slickGoTo(slideNumber),
              }
            }
          }}>
          {products.map((product, index) =>
            this.renderProductCard(product, index)
          )}
        </ReactSlick>
      </div>
    )
  }
}

const RelatedProductCarouselWithRef = React.forwardRef((props, ref) => {
  return <RelatedProductCarousel {...props} forwardedRef={ref} />
})

export { RelatedProductCarouselWithRef as RelatedProductCarousel }
export default RelatedProductCarouselWithRef
